/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable max-len */
import React, { useEffect, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { publish, JUTRO_TOPICS } from '@jutro/events';
import {
    ModalNext, ModalHeader, ModalBody, ModalFooter
} from '@jutro/components';
import { WalletService } from 'gw-capability-policy';
import { WMICLoader } from 'gw-components-platform-react';
import { WMICButton } from 'wmic-components-platform-react';
import { WMICTopics } from 'wmic-portals-utils-js';
import googleWalletBadge from 'wmic-qb-digital-theme-styles/images/en-GB_view_in_wallet_wallet-button.png';
import userProfileMessages from '../WMICInsuranceCardModal.messages';
import messages from './WMICAndroidStepsModal.messages';
import styles from './WMICAndroidStepsModal.module.scss';
import {WMICLink} from "wmic-components-amp-common-react";

const WMICAndroidStepsModal = (props) => {
    const {
        isOpen, onReject, policyNumber, termNumber, vehicles, authHeader, sessionUUID, originPage
    } = props;
    const translator = useTranslator();

    const isDetailsOrigin = originPage === 'details';
    const pageString = isDetailsOrigin
        ? 'my account:insurance:auto policy:download android auto insurance card'
        : 'my account:manage my account:download android auto insurance card';
    const [googleWalletPassUrls, updateGoogleWalletPassUrls] = useState([]);
    const [loadPassUrlPromises, updateLoadPassUrlPromises] = useState([]);
    const retrievedGWalletUrls = useRef([]);

    const getGoogleWalletPassUrl = useCallback ((vehicleNumber) => {
        const tokenPromises = [...loadPassUrlPromises];
        tokenPromises[vehicleNumber] = WalletService.getJwtToken(policyNumber, termNumber, vehicleNumber, authHeader, sessionUUID);
        tokenPromises[vehicleNumber].then((response) => response.json()).then((response) => {
            if (response && response.passUrl) {
                const urls = [...retrievedGWalletUrls.current];
                urls[vehicleNumber] = response.passUrl;
                retrievedGWalletUrls.current = urls;
                updateGoogleWalletPassUrls(retrievedGWalletUrls.current);
            }
        });
        updateLoadPassUrlPromises(tokenPromises)
    }, [authHeader, googleWalletPassUrls, loadPassUrlPromises]);

    useEffect(() => {
        // "Download Android Auto Insurance Card" modal displayed after clicking the "Android customers" CTA.
        publish(WMICTopics.ROUTE_CHANGED, {
            channel: 'my account',
            page: pageString,
            subchannel1: isDetailsOrigin ? 'insurance' : 'manage my account',
            ...(isDetailsOrigin && { subchannel2: 'auto policy' }),
            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
            state: (window.wins_data_layer?.state || '').toString().toLowerCase()
        });

        // loop through all vehicles and get the pass URL for them in preparation to render the button
        vehicles.forEach((vehicle) => {
            getGoogleWalletPassUrl(vehicle.vehicleNumber);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendClickEvent = () => {
        publish(JUTRO_TOPICS.BUTTON_CLICKED, { trackButtonIdentifier: translator(messages.downloadAndroidIdCardTrackButtonIdentifier)});
        // "View in Google Wallet" CTA clicked on the Download Android Auto Insurance Card modal.
        publish(WMICTopics.CTA_CLICK, {
            channel: 'my account',
            cta: `${pageString}-add to google wallet`,
            page: pageString,
            subchannel1: 'manage my account',
            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
            state: (window.wins_data_layer?.state || '').toString().toLowerCase()
        });
    };

    const onGetItOnGooglePlayClick = () => {
        // "Get It On Google Play" CTA clicked on the Download Android Auto Insurance Card modal.
        publish(WMICTopics.CTA_CLICK, {
            channel: 'my account',
            cta: `${pageString}-get it on google play`,
            page: pageString,
            subchannel1: 'manage my account',
            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
            state: (window.wins_data_layer?.state || '').toString().toLowerCase()
        });
    };

    const handleClose = () => {
        onReject();
    };
    
    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} shouldCloseOnEsc>
            <ModalHeader
                title={translator(userProfileMessages.insuranceIDCard)}
                contentLayout={{
                    component: 'grid',
                    componentProps: {
                        wrap: 'true'
                    }
                }}
            />
            <ModalBody>
                <div className={styles['digital-wallet']}>
                    <h3 className={styles['ww-editorial-subhead']}>{translator(messages.followTheseSteps)}</h3>
                    <ol className={styles['wmic-ol']}>
                        <li>
                            {translator(messages.usingYourAndroidDevice)}
                            <br/>
                            <WMICLink className={styles['ww-store-badge']} onClick={onGetItOnGooglePlayClick} trackButtonIdentifier={translator(messages.downloadGoogleWalletTrackButtonIdentifier)} target="_blank" href="https://play.google.com/store/apps/details?id=com.google.android.apps.walletnfcrel">
                                <img src={googleWalletBadge} alt="Google Wallet" />
                            </WMICLink>
                        </li>
                        <li>{translator(messages.onceGoogleWalletInstalled)}</li>
                        <li>{translator(messages.theCardIsNowAdded)}</li>
                    </ol>
                    <h3 className={styles['ww-editorial-subhead']}>{translator(messages.selectTheVehicle)}</h3>
                    <div>
                        {vehicles.map((vehicle) => (
                                <div id={`googleWalletButtonWrapper${vehicle.vehicleNumber}`} key={`poiButton${vehicle.vehicleNumber}`} className={styles["wmic-download-button-wrapper"]}>
                                    {googleWalletPassUrls[vehicle.vehicleNumber] ? (
                                        <>
                                            <p className={styles["wmic-sm-footing"]}>
                                                <strong>
                                                    {translator(messages.download, {
                                                        year: vehicle.year, make: vehicle.make, model: vehicle.model, vehicleNumber: vehicle.vehicleNumber
                                                    })}
                                                </strong>
                                            </p>
                                            <WMICLink 
                                                className={styles['ww-store-badge']} 
                                                onClick={sendClickEvent} 
                                                target="_blank" 
                                                href={googleWalletPassUrls[vehicle.vehicleNumber]}
                                            >
                                                <img src={googleWalletBadge} alt="Add to Google Wallet" />
                                            </WMICLink>
                                        </>
                                    ) : (
                                        <>
                                            <p className={styles["wmic-sm-footing"]}>
                                                <strong>
                                                    {translator(messages.download, {
                                                        year: vehicle.year, make: vehicle.make, model: vehicle.model, vehicleNumber: vehicle.vehicleNumber
                                                    })}
                                                </strong>
                                            </p>
                                            <WMICLoader isInline>&nbsp;</WMICLoader>
                                        </>
                                    )}
                                </div>
                            ))}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <WMICButton
                    type="primary"
                    onClick={() => onReject()}
                >
                    {translator(messages.close)}
                </WMICButton>
            </ModalFooter>
        </ModalNext>
    );
};

WMICAndroidStepsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onReject: PropTypes.func.isRequired,
    policyNumber: PropTypes.string.isRequired,
    termNumber: PropTypes.number.isRequired,
    vehicles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    sessionUUID: PropTypes.string,
    originPage: PropTypes.string
};

WMICAndroidStepsModal.defaultProps = {
    sessionUUID: null,
    originPage: 'home'
};

export default WMICAndroidStepsModal;
